import React, { useState } from "react";
import html2pdf from "html2pdf.js";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { server } from "../../server";
import { getSession } from "../../functions";

const AttendanceRegister = ({ className, section }) => {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);

  const getMonthKey = (dateStr) => {
    const [day, month, year] = dateStr.split("/");
    return `${year}-${month}`;
  };

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const groupByMonth = (students) => {
    const monthGroups = {};
    students.forEach((student) => {
      student.attendance.forEach((record) => {
        const key = getMonthKey(record.Date);
        if (!monthGroups[key]) monthGroups[key] = { students: [], dates: [] };
        if (!monthGroups[key].dates.includes(record.Date))
          monthGroups[key].dates.push(record.Date);
      });
    });
    Object.keys(monthGroups).forEach((monthKey) => {
      const filteredStudents = students.map((s) => ({
        ...s,
        attendance: s.attendance.filter(
          (r) => getMonthKey(r.Date) === monthKey,
        ),
      }));
      monthGroups[monthKey].students = filteredStudents;
    });
    return monthGroups;
  };

  const fetchData = async () => {
    const attendanceRes = await fetch(`${server}/search_attendance_mongodb`, {
      method: "POST",
      headers: {
        Authorization: `${localStorage.getItem("token")}`,
      },
      body: JSON.stringify({
        Class: className,
        Section: section,
        "From Date": new Date(fromDate).toLocaleDateString("en-GB"),
        "To Date": new Date(toDate).toLocaleDateString("en-GB"),
      }),
    });
    const attendanceJson = await attendanceRes.json();
    const students = (attendanceJson?.["body-json"]?.body || [])
      .map((s, i) => ({ ...s, rollNo: s.rollNo || i + 1 }))
      .sort((a, b) => Number(a.rollNo) - Number(b.rollNo));

    const currentSession = getSession();
    const holidayRes = await fetch(
      `${server}/GetAllDefaultHolidays?session=${currentSession}`,
      {
        method: "GET",
        headers: {
          Authorization: localStorage.getItem("token"),
          "Content-Type": "application/json",
        },
      },
    );
    const holidayJson = await holidayRes.json();
    const holidays = (holidayJson?.holidays || []).map((h) => {
      const [year, month, day] = h.date.split("-");
      return { ...h, date: `${day}/${month}/${year}` };
    });

    return { students, holidays };
  };

  const renderPageToCanvas = async (htmlContent) => {
    const wrapper = document.createElement("div");
    wrapper.style.cssText = `
      position: fixed;
      top: -99999px;
      left: -99999px;
      width: 1122px;
      background: #ffffff;
      padding: 18px 28px;
      box-sizing: border-box;
    `;
    wrapper.innerHTML = htmlContent;
    document.body.appendChild(wrapper);

    const canvas = await html2canvas(wrapper, {
      scale: 4,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
      width: 1122,
    });

    document.body.removeChild(wrapper);
    return canvas;
  };

  const pageCSS = `
    <style>
      * { box-sizing: border-box; margin: 0; padding: 0; }
      .page-header {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        border-bottom: 1.5px solid #1e3a5f;
        padding-bottom: 3px;
        margin-bottom: 5px;
      }
      .school-name { font-size: 8px; font-weight: 700; color: #1e3a5f; letter-spacing: 0.8px; text-transform: uppercase; }
      .page-meta   { font-size: 6.5px; color: #6b7a8d; margin-top: 1.5px; }
      .month-title { font-size: 11px; font-weight: 700; color: #1e3a5f; letter-spacing: 2px; text-transform: uppercase; }
      .att-table   { border-collapse: collapse; width: 100%; table-layout: fixed; }
      .att-table thead tr th {
        background: #1e3a5f; color: #e8f0fa;
        border: 0.4px solid #2d5080;
        padding: 3px 6px; text-align: center;
        font-weight: 600; font-size: 6px; letter-spacing: 0.1px;
        white-space: nowrap;
      }
      .att-table thead tr th.col-name { text-align: left; padding: 3px 6px; }
      .att-table tbody tr td {
        border: 0.4px solid #cdd8e8;
        padding: 0 3px; text-align: center;
        font-size: 6px; height: 9px; line-height: 9px;
      }
      .att-table tbody tr td.col-name {
        text-align: left; padding-left: 4px; font-size: 6px;
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      }
      .att-table tbody tr:nth-child(even) td { background: #f2f6fb; }
      .att-table tbody tr:nth-child(odd)  td { background: #ffffff; }
      .att-table tbody tr td.holiday-td {
        background: #dce8f8 !important; color: #1e3a5f;
        font-size: 5px; font-weight: 700;
        vertical-align: top; line-height: 8px;
        word-break: break-all; padding: 1px 0;
      }
      .p   { color: #15803d; font-weight: 700; }
      .a   { color: #b91c1c; font-weight: 700; }
      .phi { color: #15803d; font-weight: 700; }
      .pmd { color: #b45309; font-weight: 700; }
      .plo { color: #b91c1c; font-weight: 700; }
      .page-footer {
        margin-top: 4px; display: flex; justify-content: space-between;
        font-size: 6px; color: #8fa3b8;
        border-top: 0.5px solid #cdd8e8; padding-top: 2px;
      }
      /* Summary table — auto layout so columns breathe */
      .summary-table {
        border-collapse: collapse; width: 100%; table-layout: auto;
      }
      .summary-table thead tr th {
        background: #1e3a5f; color: #e8f0fa;
        border: 0.4px solid #2d5080;
        padding: 3px 6px; text-align: center;
        font-weight: 600; font-size: 6px; letter-spacing: 0.1px;
        white-space: nowrap;
      }
      .summary-table thead tr th.col-name { text-align: left; padding: 3px 6px; }
      .summary-table tbody tr td {
        border: 0.4px solid #cdd8e8;
        padding: 0 3px; text-align: center;
        font-size: 6px; height: 9px; line-height: 9px;
      }
      .summary-table tbody tr td.col-name {
        text-align: left; padding-left: 4px; font-size: 6px;
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      }
      .summary-table tbody tr:nth-child(even) td { background: #f2f6fb; }
      .summary-table tbody tr:nth-child(odd)  td { background: #ffffff; }
    </style>
  `;

  const buildTitlePageHtml = () => `
    ${pageCSS}
    <div style="display:flex;align-items:center;justify-content:center;height:750px;text-align:center;background:#fff;">
      <div style="border:1.5px solid #1e3a5f;padding:40px 60px;display:inline-block;">
        <div style="font-size:9px;letter-spacing:4px;text-transform:uppercase;color:#8fa3b8;margin-bottom:12px;">Academic Year &nbsp; ${getSession() || "—"}</div>
        <div style="font-size:36px;font-weight:700;color:#1e3a5f;letter-spacing:1px;line-height:1;">ATTENDANCE</div>
        <div style="font-size:36px;font-weight:300;color:#1e3a5f;letter-spacing:8px;line-height:1.2;margin-bottom:20px;">REGISTER</div>
        <div style="width:50px;height:1.5px;background:#1e3a5f;margin:0 auto 18px;"></div>
        <div style="font-size:22px;font-weight:700;color:#1e3a5f;letter-spacing:1px;">CLASS ${className} &nbsp;/&nbsp; ${section}</div>
        <div style="font-size:8px;color:#8fa3b8;margin-top:14px;letter-spacing:2px;text-transform:uppercase;">
          ${new Date(fromDate).toLocaleDateString("en-GB")} &nbsp;—&nbsp; ${new Date(toDate).toLocaleDateString("en-GB")}
        </div>
      </div>
    </div>
  `;

  const buildMonthPageHtml = (
    monthLabel,
    monthStudents,
    dates,
    holidays,
    workingDays,
  ) => {
    let rowsHtml = "";
    monthStudents.forEach((student, studentIndex) => {
      rowsHtml += `<tr>
        <td style="width:14px;">${studentIndex + 1}</td>
        <td style="width:16px;">${student.rollNo}</td>
        <td style="width:24px;">${student.admission_no}</td>
        <td class="col-name" style="width:65px;">${student.name}</td>`;

      dates.forEach((d) => {
        const holiday = holidays.find(
          (h) => h.date === d && h.class.includes(className),
        );
        if (holiday) {
          if (studentIndex === 0) {
            const vt = holiday.occasion.split("").join("<br>");
            rowsHtml += `<td rowspan="${monthStudents.length}" class="holiday-td" style="width:9px;">${vt}</td>`;
          }
          return;
        }
        const rec = student.attendance.find((r) => r.Date === d);
        if (rec?.SchoolStatus === "Present") rowsHtml += `<td class="p">P</td>`;
        else if (rec?.SchoolStatus === "Absent")
          rowsHtml += `<td class="a">A</td>`;
        else rowsHtml += `<td></td>`;
      });

      let present = 0,
        absent = 0;
      dates.forEach((d) => {
        if (holidays.find((h) => h.date === d && h.class.includes(className)))
          return;
        const rec = student.attendance.find((r) => r.Date === d);
        if (rec?.SchoolStatus === "Present") present++;
        else if (rec?.SchoolStatus === "Absent") absent++;
      });

      const pct =
        present + absent > 0
          ? Math.ceil((present / (present + absent)) * 100)
          : null;
      const pc =
        pct === null ? "" : pct >= 85 ? "phi" : pct >= 75 ? "pmd" : "plo";

      rowsHtml += `
        <td class="p"  style="width:14px;">${present}</td>
        <td class="a"  style="width:14px;">${absent}</td>
        <td class="${pc}" style="width:16px;">${pct === null ? "-" : pct + "%"}</td>
      </tr>`;
    });

    return `
      ${pageCSS}
      <div class="page-header">
        <div>
          <div class="school-name">Acharyakulam &nbsp;·&nbsp; Attendance Register</div>
          <div class="page-meta">Class ${className} &nbsp;·&nbsp; Section ${section} &nbsp;·&nbsp; ${monthStudents.length} Students &nbsp;·&nbsp; ${workingDays} Working Days</div>
        </div>
        <div class="month-title">${monthLabel}</div>
      </div>
      <table class="att-table">
        <thead>
          <tr>
            <th style="width:14px;">Sl.</th>
            <th style="width:16px;">Roll</th>
            <th style="width:24px;">Adm. No</th>
            <th class="col-name" style="width:65px;">Student Name</th>
            ${dates.map((d) => `<th style="width:9px;">${d.split("/")[0]}</th>`).join("")}
            <th style="width:14px;">P</th>
            <th style="width:14px;">A</th>
            <th style="width:16px;">%</th>
          </tr>
        </thead>
        <tbody>${rowsHtml}</tbody>
      </table>
      <div class="page-footer">
        <span>P = Present &nbsp;·&nbsp; A = Absent &nbsp;·&nbsp; Shaded = Holiday</span>
        <span>${monthLabel}</span>
      </div>
    `;
  };

  const buildSummaryPageHtml = (summaryRows, overallWorkingDays) => {
    let rowsHtml = "";
    summaryRows.forEach((s, idx) => {
      const pct =
        s.totalPresent + s.totalAbsent > 0
          ? Math.ceil((s.totalPresent / (s.totalPresent + s.totalAbsent)) * 100)
          : null;
      const pc =
        pct === null ? "" : pct >= 85 ? "phi" : pct >= 75 ? "pmd" : "plo";
      rowsHtml += `<tr>
        <td>${idx + 1}</td>
        <td>${s.rollNo}</td>
        <td>${s.admission_no}</td>
        <td class="col-name">${s.name}</td>
        <td>${overallWorkingDays}</td>
        <td class="p">${s.totalPresent}</td>
        <td class="a">${s.totalAbsent}</td>
        <td class="${pc}">${pct === null ? "—" : pct + "%"}</td>
      </tr>`;
    });

    return `
      ${pageCSS}
      <div class="page-header">
        <div>
          <div class="school-name">Acharyakulam &nbsp;·&nbsp; Attendance Register</div>
          <div class="page-meta">Class ${className} &nbsp;·&nbsp; Section ${section} &nbsp;·&nbsp; ${summaryRows.length} Students &nbsp;·&nbsp; ${overallWorkingDays} Total Working Days</div>
        </div>
        <div class="month-title">ATTENDANCE SUMMARY</div>
      </div>
      <table class="summary-table">
        <thead>
          <tr>
            <th style="min-width:20px;">Sl.</th>
            <th style="min-width:24px;">Roll</th>
            <th style="min-width:50px;">Adm. No</th>
            <th class="col-name" style="width:100%;">Student Name</th>
            <th style="min-width:60px;">Working Days</th>
            <th style="min-width:40px;">Present</th>
            <th style="min-width:40px;">Absent</th>
            <th style="min-width:36px;">%</th>
          </tr>
        </thead>
        <tbody>${rowsHtml}</tbody>
      </table>
      <div class="page-footer">
        <span>Green ≥ 85% &nbsp;·&nbsp; Orange 75–84% &nbsp;·&nbsp; Red &lt; 75%</span>
        <span>Generated: ${new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}</span>
      </div>
    `;
  };

  const generatePDF = async () => {
    if (!fromDate || !toDate) {
      alert("Please select date range");
      return;
    }
    setLoading(true);

    try {
      const { students, holidays } = await fetchData();
      const monthGroups = groupByMonth(students);

      const studentSummaryMap = {};
      students.forEach((s) => {
        studentSummaryMap[s.admission_no] = {
          name: s.name,
          rollNo: s.rollNo,
          admission_no: s.admission_no,
          totalPresent: 0,
          totalAbsent: 0,
          totalWorking: 0,
        };
      });

      const pages = [];
      pages.push(buildTitlePageHtml());

      Object.keys(monthGroups)
        .sort()
        .forEach((monthKey) => {
          let { students: monthStudents, dates } = monthGroups[monthKey];

          dates.sort((a, b) => {
            const [da, ma, ya] = a.split("/").map(Number);
            const [db, mb, yb] = b.split("/").map(Number);
            return new Date(ya, ma - 1, da) - new Date(yb, mb - 1, db);
          });

          monthStudents.forEach((s, idx) => {
            s.rollNo = idx + 1;
          });

          const [year, month] = monthKey.split("-");
          const monthLabel = `${monthNames[parseInt(month, 10) - 1]} ${year}`;

          let workingDays = 0;
          dates.forEach((d) => {
            if (
              !holidays.find((h) => h.date === d && h.class.includes(className))
            )
              workingDays++;
          });

          monthStudents.forEach((student) => {
            let present = 0,
              absent = 0;
            dates.forEach((d) => {
              if (
                holidays.find(
                  (h) => h.date === d && h.class.includes(className),
                )
              )
                return;
              const rec = student.attendance.find((r) => r.Date === d);
              if (rec?.SchoolStatus === "Present") present++;
              else if (rec?.SchoolStatus === "Absent") absent++;
            });
            if (studentSummaryMap[student.admission_no]) {
              studentSummaryMap[student.admission_no].totalPresent += present;
              studentSummaryMap[student.admission_no].totalAbsent += absent;
              studentSummaryMap[student.admission_no].totalWorking +=
                workingDays;
            }
          });

          pages.push(
            buildMonthPageHtml(
              monthLabel,
              monthStudents,
              dates,
              holidays,
              workingDays,
            ),
          );
        });

      const summaryRows = Object.values(studentSummaryMap).sort(
        (a, b) => Number(a.rollNo) - Number(b.rollNo),
      );
      const overallWorkingDays =
        summaryRows.length > 0 ? summaryRows[0].totalWorking : 0;
      pages.push(buildSummaryPageHtml(summaryRows, overallWorkingDays));

      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });
      const pdfW = 297;
      const pdfH = 210;

      for (let i = 0; i < pages.length; i++) {
        const canvas = await renderPageToCanvas(pages[i]);
        const imgData = canvas.toDataURL("image/jpeg", 0.92);
        if (i > 0) pdf.addPage();

        const canvasAspect = canvas.width / canvas.height;
        const pageAspect = pdfW / pdfH;
        let drawW = pdfW,
          drawH = pdfH,
          offsetX = 0,
          offsetY = 0;
        if (canvasAspect > pageAspect) {
          drawH = pdfW / canvasAspect;
          offsetY = (pdfH - drawH) / 2;
        } else {
          drawW = pdfH * canvasAspect;
          offsetX = (pdfW - drawW) / 2;
        }
        pdf.addImage(imgData, "JPEG", offsetX, offsetY, drawW, drawH);
      }

      pdf.save(`AttendanceRegister_${className}_${section}.pdf`);
    } catch (error) {
      console.error(error);
      alert("Failed to generate PDF");
    }

    setLoading(false);
  };

  return (
    <div>
      <div className="bg-secondary-soft rounded-4 py-3 px-4 mt-1">
        <div>
          <input
            type="date"
            className="form-control ps-4 pe-5 border-0 rounded-pill"
            placeholder="..."
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
          <input
            type="date"
            className="form-control ps-4 pe-5 border-0 rounded-pill mt-1"
            placeholder="..."
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>
        <button
          onClick={generatePDF}
          disabled={loading}
          className="btn-download mt-2"
        >
          {loading ? (
            <>
              <div
                className="spinner-border spinner-border-sm me-1"
                role="status"
              ></div>
              Generating
            </>
          ) : (
            <>Download Register</>
          )}
        </button>
      </div>
    </div>
  );
};

export default AttendanceRegister;
